import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from '../utils/api';

/**
 * MadeWholeWaiverModal
 *
 * Generates a Made-Whole Doctrine waiver letter for ONE lienholder.
 * Opened from the "Waiver letter" button on a Liens tab row.
 *
 * Pre-fills from two sources and lets the attorney edit everything:
 *   - the lien row itself (carrier, adjuster, claim #, lien amount)
 *   - /letter-context (client name, DOL, case financials, and a draft
 *     injury narrative assembled from the treating providers)
 *
 * The injury narrative is the part that actually needs an attorney. The
 * draft names the treating providers and leaves a bracketed instruction
 * for the clinical detail — if it's left in place the reviewer will see
 * it in the .docx before anything goes out.
 */
function MadeWholeWaiverModal({ modal, clientName, close }) {
  const { lien, projectId } = modal;

  const [ctx, setCtx] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const [form, setForm] = useState({
    carrier_name: lien?.lienholder_name || '',
    carrier_address: lien?.lienholder_address || '',
    adjuster_name: lien?.adjuster_name || '',
    carrier_email: lien?.lienholder_email || '',
    carrier_fax: '',
    client_name: clientName || '',
    client_title: '',
    reference_number: lien?.claim_number || lien?.file_number || '',
    date_of_incident: '',
    incident_description: 'motor vehicle collision',
    settlement_amount: '',
    is_policy_limits: false,
    attorney_fees_pct: '',
    procurement_expenses: '',
    medical_expenses: '',
    injury_narrative: '',
    delivery_method: 'SENT VIA FAX',
  });

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(
          `${API_URL}/api/projects/${projectId}/letter-context`,
          { withCredentials: true }
        );
        if (cancelled) return;
        const c = res.data;
        setCtx(c);
        // Fill only the fields the lien row didn't already provide.
        setForm(prev => ({
          ...prev,
          client_name: prev.client_name || c.client_name || '',
          date_of_incident: prev.date_of_incident || c.date_of_incident || '',
          settlement_amount: prev.settlement_amount ||
            (c.settlement_amount != null ? String(c.settlement_amount) : ''),
          attorney_fees_pct: prev.attorney_fees_pct ||
            (c.attorney_fees_pct != null ? String(c.attorney_fees_pct) : ''),
          procurement_expenses: prev.procurement_expenses ||
            (c.legal_costs != null ? String(c.legal_costs) : ''),
          medical_expenses: prev.medical_expenses ||
            (c.total_medical_expenses != null ? String(Math.round(c.total_medical_expenses * 100) / 100) : ''),
          injury_narrative: prev.injury_narrative || c.draft_injury_narrative || '',
        }));
      } catch (e) {
        if (!cancelled) setError('Could not pre-fill from case: ' + (e.response?.data?.detail || e.message));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [projectId]);

  const num = (v) => {
    if (v === '' || v === null || v === undefined) return null;
    const n = parseFloat(v);
    return isNaN(n) ? null : n;
  };

  const generate = async () => {
    setBusy(true);
    setError(null);
    try {
      const payload = {
        carrier_name: form.carrier_name,
        carrier_address_lines: (form.carrier_address || '').split('\n').filter(Boolean),
        adjuster_name: form.adjuster_name || null,
        carrier_email: form.carrier_email || null,
        carrier_fax: form.carrier_fax || null,
        client_name: form.client_name,
        client_title: form.client_title || null,
        reference_number: form.reference_number || null,
        date_of_incident: form.date_of_incident || null,
        incident_description: form.incident_description || null,
        settlement_amount: num(form.settlement_amount),
        is_policy_limits: !!form.is_policy_limits,
        attorney_fees_pct: num(form.attorney_fees_pct),
        procurement_expenses: num(form.procurement_expenses),
        medical_expenses: num(form.medical_expenses),
        injury_narrative: form.injury_narrative || null,
        delivery_method: form.delivery_method,
      };
      const res = await axios.post(
        `${API_URL}/api/projects/${projectId}/letters/made-whole-waiver`,
        payload,
        { withCredentials: true, responseType: 'blob' }
      );
      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Waiver Made Whole_ ${form.client_name}.docx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      close();
    } catch (e) {
      let msg = e.message;
      if (e.response?.data instanceof Blob) {
        try { msg = JSON.parse(await e.response.data.text()).detail || msg; } catch (_) { /* keep */ }
      } else if (e.response?.data?.detail) {
        msg = e.response.data.detail;
      }
      setError('Letter generation failed: ' + msg);
    } finally {
      setBusy(false);
    }
  };

  const narrativeHasPlaceholder = (form.injury_narrative || '').includes('[');
  const canGenerate = form.carrier_name.trim() && form.client_name.trim() && !busy;

  return (
    // Rendered inside the case overlay, which closes the case on any
    // click that reaches it. Without this every click in here — a
    // field, a label, anything — bubbled up and threw the preparer
    // back to the case list mid-form.
    <div style={s.backdrop}
         onClick={(e) => { e.stopPropagation(); close(); }}>
      <div style={s.modal} onClick={(e) => e.stopPropagation()}>
        <div style={s.title}>Made-whole waiver letter</div>
        <div style={s.subtitle}>
          To <strong>{lien?.lienholder_name || 'the lienholder'}</strong>. Argues under
          California's Made Whole Doctrine that the client hasn't been fully
          compensated, so the carrier can't enforce its lien.
        </div>

        {loading && <div style={s.loading}>Pre-filling from case…</div>}
        {error && <div style={s.error}>{error}</div>}

        <Section label="Recipient" />
        <Row>
          <Field label="Carrier / lienholder" required value={form.carrier_name} onChange={(v) => set('carrier_name', v)} />
          <Field label="Attn: adjuster" value={form.adjuster_name} onChange={(v) => set('adjuster_name', v)} />
        </Row>
        <Field label="Carrier address" textarea value={form.carrier_address} onChange={(v) => set('carrier_address', v)}
               hint="One line per row. Pre-filled from the lien record." />
        <Row>
          <Field label="Email" value={form.carrier_email} onChange={(v) => set('carrier_email', v)} />
          <Field label="Fax" value={form.carrier_fax} onChange={(v) => set('carrier_fax', v)} placeholder="(844) 634-2520" />
        </Row>
        <Row>
          <Field label="Reference #" value={form.reference_number} onChange={(v) => set('reference_number', v)} />
          <SelectField label="Delivery method" value={form.delivery_method} onChange={(v) => set('delivery_method', v)}
                       options={['SENT VIA FAX', 'SENT VIA U.S. MAIL', 'SENT VIA EMAIL']} />
        </Row>

        <Section label="Client" />
        <Row>
          <Field label="Client name" required value={form.client_name} onChange={(v) => set('client_name', v)} />
          <SelectField label="Courtesy title" value={form.client_title} onChange={(v) => set('client_title', v)}
                       options={['', 'Mr.', 'Ms.', 'Mx.', 'Dr.']} />
        </Row>
        <Row>
          <Field label="Date of incident" value={form.date_of_incident} onChange={(v) => set('date_of_incident', v)} />
          <Field label="Incident type" value={form.incident_description} onChange={(v) => set('incident_description', v)} />
        </Row>

        <Section label="Financials" />
        <Row>
          <Field label="Settlement amount ($)" decimal value={form.settlement_amount} onChange={(v) => set('settlement_amount', v)} />
          <Field label="Attorney fees (%)" decimal value={form.attorney_fees_pct} onChange={(v) => set('attorney_fees_pct', v)} />
        </Row>
        <Row>
          <Field label="Procurement expenses ($)" decimal value={form.procurement_expenses} onChange={(v) => set('procurement_expenses', v)} />
          <Field label="Medical expenses ($)" decimal value={form.medical_expenses} onChange={(v) => set('medical_expenses', v)}
                 hint="Summed from the Meds tab." />
        </Row>
        <label style={s.checkRow}>
          <input type="checkbox" checked={form.is_policy_limits}
                 onChange={(e) => set('is_policy_limits', e.target.checked)} />
          <span>Settled for policy limits</span>
        </label>

        <Section label="Injury narrative" />
        <Field
          label="Course of treatment, symptoms, and prognosis"
          textarea rows={5}
          value={form.injury_narrative}
          onChange={(v) => set('injury_narrative', v)}
          hint="Drafted from the treating providers on this case — edit before sending."
        />
        {narrativeHasPlaceholder && (
          <div style={s.warn}>
            The narrative still contains a bracketed placeholder. Replace it before
            this letter goes out.
          </div>
        )}

        <div style={s.actions}>
          <button type="button" style={s.cancel} onClick={close}>Cancel</button>
          <button
            type="button"
            style={{ ...s.primary, opacity: canGenerate ? 1 : 0.5 }}
            disabled={!canGenerate}
            onClick={generate}
          >
            {busy ? 'Generating…' : 'Generate .docx'}
          </button>
        </div>
      </div>
    </div>
  );
}

const Section = ({ label }) => (
  <div style={s.section}>{label}</div>
);

const Row = ({ children }) => (
  <div style={s.row}>{children}</div>
);

function Field({ label, value, onChange, textarea, rows = 3, hint, required, placeholder, decimal }) {
  return (
    <div style={{ flex: 1, minWidth: 0, marginBottom: 10 }}>
      <label style={s.label}>
        {label}{required && <span style={{ color: '#dc2626' }}> *</span>}
      </label>
      {textarea ? (
        <textarea value={value} rows={rows} onChange={(e) => onChange(e.target.value)}
                  style={s.textarea} placeholder={placeholder || ''} />
      ) : (
        <input
          type="text"
          inputMode={decimal ? 'decimal' : undefined}
          value={value}
          onChange={(e) => {
            if (decimal) {
              const raw = e.target.value;
              if (!/^[0-9.]*$/.test(raw)) return;
              if ((raw.match(/\./g) || []).length > 1) return;
              onChange(raw);
            } else {
              onChange(e.target.value);
            }
          }}
          style={s.input}
          placeholder={placeholder || ''}
        />
      )}
      {hint && <div style={s.hint}>{hint}</div>}
    </div>
  );
}

function SelectField({ label, value, onChange, options }) {
  return (
    <div style={{ flex: 1, minWidth: 0, marginBottom: 10 }}>
      <label style={s.label}>{label}</label>
      <select value={value} onChange={(e) => onChange(e.target.value)} style={s.input}>
        {options.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}

const s = {
  backdrop: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(0,0,0,0.35)', display: 'flex',
    alignItems: 'center', justifyContent: 'center', zIndex: 1100, padding: 20,
  },
  modal: {
    background: 'white', borderRadius: 12, padding: '24px 28px',
    width: 'min(680px, 95vw)', maxHeight: '88vh', overflowY: 'auto',
    boxShadow: '0 12px 32px rgba(0,0,0,0.2)',
  },
  title: {
    fontFamily: "'Arial Black', Impact, sans-serif",
    fontSize: 18, color: '#171D29', marginBottom: 4,
  },
  subtitle: { fontSize: 13, color: '#6b7585', lineHeight: 1.5, marginBottom: 8 },
  loading: { fontSize: 12, color: '#6b7585', marginBottom: 10 },
  error: {
    background: '#fee2e2', color: '#991b1b', padding: '8px 12px',
    borderRadius: 6, marginBottom: 12, fontSize: 12,
  },
  warn: {
    background: '#fef3c7', color: '#92400e', padding: '8px 12px',
    borderRadius: 6, marginTop: 4, marginBottom: 8, fontSize: 12, lineHeight: 1.45,
  },
  section: {
    fontFamily: "'Arial Black', Impact, sans-serif",
    fontSize: 11, color: '#A1B808', letterSpacing: 1,
    textTransform: 'uppercase', margin: '16px 0 8px 0',
    borderTop: '1px solid #f3f4f6', paddingTop: 12,
  },
  row: { display: 'flex', gap: 12, flexWrap: 'wrap' },
  label: {
    display: 'block', fontSize: 10, color: '#6b7585',
    textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700, marginBottom: 4,
  },
  input: {
    width: '100%', boxSizing: 'border-box', padding: '8px 10px',
    border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13,
    color: '#171D29', fontFamily: 'inherit',
  },
  textarea: {
    width: '100%', boxSizing: 'border-box', padding: '8px 10px',
    border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13,
    color: '#171D29', fontFamily: 'inherit', resize: 'vertical',
  },
  hint: { fontSize: 11, color: '#9ca3af', marginTop: 3, lineHeight: 1.4 },
  checkRow: {
    display: 'flex', alignItems: 'center', gap: 8,
    fontSize: 13, color: '#171D29', marginBottom: 6, cursor: 'pointer',
  },
  actions: { display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 },
  cancel: {
    padding: '8px 14px', border: '1px solid #d1d5db', background: 'white',
    color: '#171D29', borderRadius: 6, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit',
  },
  primary: {
    padding: '8px 16px', border: '1px solid #A1B808', background: '#A1B808',
    color: '#171D29', borderRadius: 6, fontSize: 13, fontWeight: 600,
    cursor: 'pointer', fontFamily: 'inherit',
  },
};

export default MadeWholeWaiverModal;
